// Paleta corporativa AURA (navy profundo + cian). Sistema de 3 tonos de marca:
//   mathiz = navy profundo (color primario: encabezados, botones, texto de marca)
//   accent = cian profundo   (interactivo y LEGIBLE en blanco: activo de sidebar,
//                             botones "+Agregar", acentos de UI)
//   gold   = cian brillante  (decorativo: barras superiores, subrayados, gradientes
//                             con navy) — conserva el nombre "gold" por compatibilidad.
// Los colores semánticos (red=error, amber=aviso, success/verde) NO cambian.
export const C = {
  bg:"#f5f7fb", card:"#ffffff", border:"#e2e8f0",
  accent:"#0E7490", amber:"#F59E0B", red:"#EF4444",
  blue:"#2B4A82", purple:"#4C5FA8", cyan:"#0E7490",
  mathiz:"#12264D", gold:"#00C2E0",
  text:"#0f172a", muted:"#64748b", dim:"#94a3b8"
};

// ── Tokens de diseño AURA ───────────────────────────────────────────────────
// Fuente única de verdad de la FORMA (el color vive en C). Densidad "nivel 2 ·
// Denso": compacta pero cómoda para jornadas largas. Cambiar estos valores
// reestiliza todo el ERP, porque los helpers compartidos (btnStyle, inputStyle,
// searchBox, thSortable) y los módulos se apoyan en ellos.
export const T = {
  // radios
  r:  { sm:"5px", md:"6px", lg:"8px", pill:"999px" },
  // escala de espaciado
  sp: { 1:"3px", 2:"5px", 3:"8px", 4:"11px", 5:"14px", 6:"18px" },
  // elevación
  sh: { sm:"0 1px 2px rgba(11,19,43,.06)", md:"0 2px 8px rgba(11,19,43,.08)" },
  // tipografía
  fs: { xs:"9.5px", sm:"10.5px", md:"11.5px", lg:"12.5px", xl:"15px" },
  // alto de fila / celda de tabla
  cell: "5px 9px"
};

// Distintivo por tipo de documento, DENTRO de la familia navy-cian de AURA.
// Antes cada documento tenía un color pleno del arcoíris (azul, ámbar, morado,
// cian, verde, rojo), lo que competía con los colores de estado: un encabezado
// verde permanente hace que el verde deje de significar "pagada", y un rojo fijo
// que el rojo deje de significar "vencida". Estos seis tonos son apagados y
// emparentados: distinguen el flujo sin gritar, y dejan el color saturado
// disponible para lo único que debe saltar a la vista — el estado.
export const DOC_COLORS = {
  CT:   "#2563A8",  // azul
  OP:   "#0F766E",  // teal
  OCP:  "#4C5FA8",  // índigo apagado
  RC:   "#0E7490",  // cian profundo
  FACT: "#12264D",  // navy AURA (el documento principal)
  NC:   "#64748B"   // slate (el reverso de la factura)
};

// Identidad del producto (white-label): al vender el ERP a otra empresa,
// el script scripts/nueva-marca.mjs actualiza estos valores junto con los
// colores C.mathiz/C.gold, el logo (index.html) y las credenciales de
// Supabase. Ver docs/INSTALACION_WHITE_LABEL.md.
export const BRAND = {
  producto: "AURA ENTERPRISE ERP",
  slogan: "Control absoluto de tu negocio, Aura mismo.",
  archivo_prefijo: "AURA"   // prefijo de los archivos exportados (CSV/Excel)
};

// Identidad de la empresa que opera el ERP. Los datos de identificación
// arrancan VACÍOS: se diligencian en Configuración → Datos de la empresa
// (paso 1 de la Puesta en Marcha) y se guardan en erp:empresa_config,
// sincronizado a la nube. Un sistema recién reiniciado no debe mostrar los
// datos de otra empresa en pantallas ni documentos.
export const EMPRESA = {
  nombre:"",
  nit:"",
  dv:"",
  direccion:"",
  telefono:"",
  email:"",
  tipo_persona:"Jurídica",
  regimen_iva:"responsable",       // responsable | no_responsable
  gran_contribuyente:false,
  autorretenedor:false,
  actividad_economica:"",          // código CIIU
  ciudad_dian:"",

  // Responsabilidades de la casilla 53 del RUT que describen a la PROPIA empresa
  // (las de Parámetros Fiscales describen cómo tratar a los terceros).
  regimen_simple:false,            // resp. 47 — Régimen Simple de Tributación
  agente_retencion_renta:false,    // resp. 07 — agente de retención a título de renta
  agente_retencion_iva:false,      // resp. 09 — agente de retención de IVA
  obligado_contabilidad:false,     // resp. 42
  facturador_electronico:false,    // resp. 52
  responsabilidades:[],            // códigos leídos del RUT, p. ej. ["48","42","52"]

  // Registro mercantil (certificado de Cámara de Comercio). La renovación es
  // obligatoria entre el 1 de enero y el 31 de marzo de cada año, así que del
  // certificado solo interesa el ÚLTIMO AÑO RENOVADO: con él se calcula el
  // próximo vencimiento y se avisa antes de que se pase la fecha.
  matricula_mercantil:"",
  matricula_anio_renovado:"",      // "2026"
  representante_legal:""
  // logo: dataURL del logo subido por el usuario (opcional; ver Configuración)
};

// Copia intacta de los valores de fábrica, tomada antes de que la configuración
// guardada mute EMPRESA (Object.assign). La usa "Limpiar todo y reiniciar".
// El spread es superficial: `responsabilidades` se clona aparte para que
// "Limpiar todo y reiniciar" no deje a EMPRESA compartiendo el mismo arreglo con
// su copia de fábrica.
export const EMPRESA_DEFAULT = { ...EMPRESA, responsabilidades: [] };

// Identidad visual base del producto: AURA ERP. Grid de módulos con acento cian
// sobre azul profundo. Es el logo por defecto que se muestra mientras un tenant
// no haya subido el suyo (EMPRESA.logo); cada cliente puede cargar el propio.
//
// Paleta Aura: navy profundo #0B132B → #12264D (fondo con degradado), acento
// cian #22ECFF → #00B8D9 (baldosa destacada) y azules #2B4A82 / #3A506B para
// las demás baldosas (contraste visible sobre el navy). Antes las baldosas
// oscuras casi no se distinguían del fondo casi negro.
const AURA_DEFS =
  `<defs>` +
  `<linearGradient id="auraBg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#0B132B"/><stop offset="1" stop-color="#12264D"/></linearGradient>` +
  `<linearGradient id="auraCy" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#22ECFF"/><stop offset="1" stop-color="#00B8D9"/></linearGradient>` +
  `</defs>`;

// Grid 2x2 de baldosas redondeadas, con la superior-izquierda en cian (acento).
const AURA_GRID = (x, y, s, gap) => {
  const r = (s * 0.24).toFixed(2);
  return `<rect x="${x}" y="${y}" width="${s}" height="${s}" rx="${r}" fill="url(#auraCy)"/>` +
    `<rect x="${x + s + gap}" y="${y}" width="${s}" height="${s}" rx="${r}" fill="#2B4A82"/>` +
    `<rect x="${x}" y="${y + s + gap}" width="${s}" height="${s}" rx="${r}" fill="#2B4A82"/>` +
    `<rect x="${x + s + gap}" y="${y + s + gap}" width="${s}" height="${s}" rx="${r}" fill="#3A506B"/>`;
};

// Logo horizontal (grid de módulos + "AURA / ENTERPRISE ERP"): login, encabezados, documentos.
// Grid centrado verticalmente en el recuadro (68px de alto en un lienzo de 120).
export const AURA_LOGO = "data:image/svg+xml," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="500" height="120" viewBox="0 0 500 120">` +
  AURA_DEFS +
  `<rect width="500" height="120" fill="url(#auraBg)" rx="16"/>` +
  `<g transform="translate(32,26)">` + AURA_GRID(0, 0, 30, 8) + `</g>` +
  `<text x="122" y="66" font-family="'Plus Jakarta Sans','Inter',sans-serif" font-size="46" font-weight="800" fill="#FFFFFF" letter-spacing="3">AURA</text>` +
  `<text x="124" y="89" font-family="'Inter',sans-serif" font-size="13" font-weight="700" fill="#22ECFF" letter-spacing="6.5">ENTERPRISE ERP</text>` +
  `</svg>`
);

// Isotipo cuadrado (solo el grid de módulos sobre fondo navy): barra lateral.
// El grid se centra con márgenes proporcionales (18px en un lienzo de 120).
export const AURA_ISOTIPO = "data:image/svg+xml," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">` +
  AURA_DEFS +
  `<rect width="120" height="120" fill="url(#auraBg)" rx="26"/>` +
  `<g transform="translate(18,18)">` + AURA_GRID(0, 0, 38, 8) + `</g>` +
  `</svg>`
);

export const DEFAULT_ALERT_CONFIG = {
  diasEntregaOP: 5, diasSeguimientoCT: 3, diasVencimientoCxP: 3, diasVencimientoResDian: 30,
  // La matrícula se renueva antes del 31 de marzo; 45 días de aviso dan margen
  // para tramitarla sin correr.
  diasVencimientoMatricula: 45,
  whatsapp: "", whatsappNumbers: [], numeroEnvioId: null, numerosEnvioIds: []
};

export const DEFAULT_IA_CONFIG = { model: "claude-sonnet-4-6" };

export const KEYS = {
  clientes:"erp:clientes", proveedores:"erp:proveedores", productos:"erp:productos",
  cotizaciones:"erp:ct", pedidos:"erp:op", ocp:"erp:ocp", remisiones:"erp:rc",
  facturas:"erp:fact", empleados:"erp:empleados", notas_credito:"erp:nc",
  inventario:"erp:inventario", puc:"erp:puc", asientos:"erp:asientos",
  alertas_enviadas:"erp:alertas_enviadas", resoluciones_dian:"erp:resoluciones_dian",
  nomina_liquidaciones:"erp:nomina_liquidaciones", activos_fijos:"erp:activos_fijos",
  cuentas_pagar:"erp:cuentas_pagar", cuentas_cobro:"erp:cuentas_cobro"
};

// Cargos posibles para un empleado. "vendedor" es el único que además
// participa en el módulo de comisiones (state.data.empleados filtrado por
// cargo === "vendedor" hace las veces de lo que antes era la tabla vendedores;
// además, cualquier empleado con es_vendedor=true también vende sin cambiar de
// cargo — ver getVendedores() en comisiones.js).
export const CARGOS_EMPLEADO = [
  "administrativo", "contable", "aux_bodega", "servicios_generales",
  "archivador", "recepcionista", "vendedor", "asistente_administrativo",
  "compras", "asistente_contable", "gerente"
];

export const CARGO_LABELS = {
  administrativo: "Administrativo",
  contable: "Contable",
  aux_bodega: "Auxiliar de bodega",
  servicios_generales: "Servicios generales",
  archivador: "Archivador",
  recepcionista: "Recepcionista",
  vendedor: "Vendedor",
  asistente_administrativo: "Asistente administrativo",
  compras: "Compras",
  asistente_contable: "Asistente contable",
  gerente: "Gerente"
};

export const ROLES_INFO = {
  admin:        { label: "Administrador", icon: "👑" },
  vendedor:     { label: "Vendedor",      icon: "🧑‍💼" },
  compras:      { label: "Compras",       icon: "🛒" },
  contabilidad: { label: "Contabilidad",  icon: "🧮" },
  tesoreria:    { label: "Tesorería",     icon: "💵" },
  operaciones:  { label: "Operaciones",   icon: "⚙️" }
};

// Roles para los que la verificación en dos pasos (MFA/TOTP) es obligatoria,
// no opcional — tienen acceso a información financiera sensible.
export const ROLES_MFA_OBLIGATORIO = ["admin", "contabilidad"];

export const ROLE_PAGES = {
  // Los asesores prueban el flujo piloto (pedido del usuario, 1-nov-2026).
  vendedor:     ["CT", "piloto-pedidos", "piloto-transicion", "piloto-por-ordenar", "piloto-recepciones"],
  compras:      ["OP", "OCP", "fact-prov"],
  contabilidad: ["FACT", "NC", "cartera", "cxp", "anticipos", "comisiones", "nomina", "activos-fijos", "reportes", "informes", "contabilidad", "parametros-fiscales", "fact-prov"],
  tesoreria:    ["cxp", "cartera", "anticipos"],
  operaciones:  ["CT", "OP", "RC", "OCP", "inventario", "alertas"] // ventas, compras, inventario, alertas
  // admin: sin restricción (ver tienePermiso)
};

// Catálogo de módulos (grupos) y submódulos (páginas) del ERP. Lo usan el
// menú lateral (app.js) y el editor de permisos de roles personalizados
// (usuarios.js). El dashboard queda por fuera: siempre es visible.
// Organización por proceso de negocio, en el orden del ciclo operativo:
// vender → comprar/pagar → inventario → facturar/cobrar → contabilizar →
// gestionar personas → analizar → administrar el sistema.
export const NAV_GRUPOS = [
  {
    id: "comercial", label: "Comercial", icon: "💼",
    items: [
      { id:"CT", label:"Cotizaciones",    icon:"📋", color:DOC_COLORS.CT},
      { id:"OP", label:"Órd. Producción", icon:"🔧", color:DOC_COLORS.OP},
      // La OP es el documento central: este tablero la sigue por su hilo
      // completo (entrega → factura → cobro, y lo que se debe a terceros).
      { id:"control-op", label:"Control de Órdenes", icon:"🎯"},
      { id:"RC", label:"Remisiones",      icon:"📮", color:DOC_COLORS.RC},
      { id:"asistente-ia", label:"Asistente IA", icon:"🤖"},
    ]
  },
  // Flujo alterno copiado del panel de ventas (CT → PV → RQ → OC → RP → RS),
  // en prueba al lado del de siempre. Ninguna de sus páginas está en un rol:
  // solo la ven los administradores hasta que se dé permiso en Usuarios.
  {
    id: "piloto", label: "Flujo piloto", icon: "🧪",
    items: [
      { id:"piloto-pedidos",    label:"Pedidos (PV)",           icon:"📑"},
      { id:"piloto-transicion", label:"Material en transición", icon:"🚚"},
      { id:"piloto-por-ordenar", label:"Lo que falta por ordenar", icon:"🧺"},
      { id:"piloto-recepciones", label:"Recepciones (RP)", icon:"📥"},
    ]
  },
  {
    id: "compras", label: "Compras y Pagos", icon: "🛒",
    items: [
      { id:"OCP",       label:"Órd. Compra",       icon:"🛒", color:DOC_COLORS.OCP},
      { id:"fact-prov", label:"Facturas Proveedor", icon:"🧾"},
      { id:"cxp",       label:"Cuentas por Pagar", icon:"💸"},
      { id:"anticipos", label:"Anticipos",          icon:"💵"},
    ]
  },
  {
    id: "inventarios", label: "Inventarios", icon: "📦",
    items: [
      { id:"inventario", label:"Inventario", icon:"📦"},
    ]
  },
  {
    id: "facturacion", label: "Facturación y Cartera", icon: "💰",
    items: [
      { id:"FACT",    label:"Facturación",   icon:"💰", color:DOC_COLORS.FACT},
      { id:"NC",      label:"Notas Crédito", icon:"🧾", color:DOC_COLORS.NC},
      { id:"cartera", label:"Cartera (CxC)", icon:"📊"},
    ]
  },
  {
    id: "contabilidad", label: "Contabilidad", icon: "🧮",
    items: [
      { id:"contabilidad", label:"Contabilidad",        icon:"🧮"},
      { id:"activos-fijos",label:"Activos Fijos",       icon:"🏢"},
      { id:"parametros-fiscales", label:"Parámetros Fiscales", icon:"📐"},
    ]
  },
  {
    id: "talento", label: "Talento Humano", icon: "🧑‍💼",
    items: [
      { id:"empleados",  label:"Empleados",  icon:"🧑‍💼"},
      { id:"nomina",     label:"Nómina",     icon:"🧑‍🌾"},
      { id:"comisiones", label:"Comisiones", icon:"🏆"},
    ]
  },
  {
    id: "informes", label: "Informes", icon: "📈",
    items: [
      { id:"reportes", label:"Reportes Operativos",   icon:"📈"},
      { id:"informes", label:"Informes Gerenciales",  icon:"📑"},
    ]
  },
  {
    id: "administracion", label: "Administración", icon: "⚙️",
    items: [
      { id:"puesta-en-marcha", label:"Puesta en Marcha", icon:"🚀"},
      { id:"maestros",  label:"Maestros",      icon:"📇" },
      { id:"usuarios",  label:"Usuarios",      icon:"👥"},
      { id:"auditoria", label:"Auditoría",     icon:"🕵️"},
      { id:"respaldos", label:"Respaldos",     icon:"💾" },
      { id:"config",    label:"Configuración", icon:"⚙️"},
    ]
  }
];

// Técnicas de marcación que se ofrecen en el campo "Impresión" de los
// documentos. Es una lista de SUGERENCIAS, no una restricción: el campo sigue
// aceptando cualquier texto, porque siempre aparece una técnica nueva o una
// combinación que no está aquí.
export const TECNICAS_IMPRESION = [
  "Tampografía",
  "Screen",
  "DTF UV",
  "DTF Textil",
  "Bordado",
  "Litografía",
  "Láser"
];

export const PARAMETROS_FISCALES_KEY = "erp:parametros_fiscales";

export const DEFAULT_PARAMETROS_FISCALES = {
  anio_vigencia: new Date().getFullYear(),
  uvt: 52374,                          // Valor UVT vigente (Resolución DIAN 000238 de 15-12-2025, año gravable 2026)
  iva_general_pct: 19,                 // Tarifa general de IVA (Art. 468 E.T.)
  retefuente_compras_pct: 2.5,         // % retefuente compras generales (Art. 401 E.T. / Dcto. 392 de 2021)
  // Cuantías mínimas del Dcto. 572 de 2025, que volvió a regir el 1 de julio de
  // 2026 (Consejo de Estado, Auto del 2-jun-2026, exp. 30229). Antes eran 27 y 4
  // UVT: con las viejas se DEJA DE RETENER en el tramo intermedio, y la
  // retención no practicada la responde el agente retenedor (Art. 370 E.T.).
  retefuente_compras_base_uvt: 10,     // Base mínima compras generales sujetas a retefuente
  retefuente_servicios_pct: 4,         // % retefuente servicios generales (declarantes)
  retefuente_servicios_base_uvt: 2,    // Base mínima servicios generales (Art. 392 E.T.)
  reteiva_pct: 15,                     // % de retención sobre el IVA generado (Art. 437-1 E.T.)
  retefuente_servicios_no_decl_pct: 6, // % retefuente servicios a NO declarantes de renta
  renta_pct: 35,                       // Tarifa general de renta sociedades (Art. 240 E.T.) — para la provisión estimada
  // ── ICA de la PROPIA empresa (lo dice el RIT del municipio) ──────────────
  ica_municipio: "",                   // Municipio donde se declara el ICA
  ica_tarifa_mil: 0,                   // Tarifa de la actividad, en por mil (‰)
  // ── Autorretención especial a título de renta ────────────────────────────
  // Dcto. 2201 de 2016, compilado en el DUR 1625/2016 arts. 1.2.6.6 a 1.2.6.10:
  // la practica la PROPIA sociedad sobre cada ingreso cuando está exonerada de
  // aportes parafiscales (Art. 114-1 E.T.). La tarifa depende del CIIU de su
  // actividad principal: 0,40% / 0,80% / 1,60%. No reemplaza la retención que
  // le practican los clientes: se suma a ella.
  autorretencion_renta_activa: false,
  autorretencion_renta_pct: 0,
  cuenta_cobro_tope: 1700000,          // Tope mensual por persona en cuentas de cobro (control interno de la empresa)
  rst_exonera_retefuente: true,        // RST no es sujeto de retefuente a título de renta (Art. 911 E.T., Ley 2010/2019)
  rst_exonera_reteica: true,           // El componente de ICA queda integrado al RST en la mayoría de municipios
  autorretenedor_exonera_retefuente: true, // Autorretenedores no se les practica retefuente (se autorretienen)
  notas: "Actualizar cada enero según resolución DIAN de UVT y normas vigentes de retención."
};

export const REGIMENES_FISCALES = [
  { value:"ordinario",         label:"Régimen Ordinario" },
  { value:"rst",                label:"Régimen Simple de Tributación (RST)" },
  { value:"regimen_especial",  label:"Régimen Tributario Especial (ESAL)" },
  { value:"no_declarante",     label:"Persona Natural — No declarante de renta" }
];

export const TIPOS_CONTRATO = ["indefinido", "fijo", "obra_labor", "aprendizaje"];

// Categorías de activo fijo (grupo 15 del PUC). vidaUtilFiscalMeses es la vida
// útil SUGERIDA según las tasas máximas de depreciación fiscal (Art. 137 E.T. +
// Decreto 1625/2016, art. 1.2.1.18.4) — es un tope de referencia editable por
// activo, no un valor obligatorio; debe soportarse técnicamente igual.
export const CATEGORIAS_ACTIVO_FIJO = [
  { value:"terreno",        label:"Terrenos",                           cuentaActivo:"1504", depreciable:false, vidaUtilFiscalMeses:null },
  { value:"construccion",   label:"Construcciones y edificaciones",     cuentaActivo:"1516", depreciable:true,  vidaUtilFiscalMeses:540  },
  { value:"maquinaria",     label:"Maquinaria y equipo",                cuentaActivo:"1520", depreciable:true,  vidaUtilFiscalMeses:120  },
  { value:"equipo_oficina", label:"Equipo de oficina",                  cuentaActivo:"1524", depreciable:true,  vidaUtilFiscalMeses:120  },
  { value:"equipo_computo", label:"Equipo de cómputo y comunicaciones", cuentaActivo:"1528", depreciable:true,  vidaUtilFiscalMeses:60   },
  { value:"flota",          label:"Flota y equipo de transporte",       cuentaActivo:"1540", depreciable:true,  vidaUtilFiscalMeses:120  }
];

export const MOTIVOS_BAJA_ACTIVO = [
  { value:"venta",         label:"Venta" },
  { value:"obsolescencia", label:"Obsolescencia / fin de vida útil" },
  { value:"perdida",       label:"Pérdida o hurto" },
  { value:"donacion",      label:"Donación" }
];

export const TIPO_CONTRATO_LABELS = {
  indefinido: "Término indefinido",
  fijo: "Término fijo",
  obra_labor: "Obra o labor",
  aprendizaje: "Contrato de aprendizaje"
};

export const NIVELES_RIESGO_ARL = ["I", "II", "III", "IV", "V"];

export const NIVEL_RIESGO_ARL_LABELS = {
  I: "Riesgo I — Mínimo",
  II: "Riesgo II — Bajo",
  III: "Riesgo III — Medio",
  IV: "Riesgo IV — Alto",
  V: "Riesgo V — Máximo"
};

export const PERIODICIDAD_NOMINA = ["mensual", "quincenal"];

export const PERIODICIDAD_NOMINA_LABELS = {
  mensual: "Mensual",
  quincenal: "Quincenal"
};

export const PARAMETROS_NOMINA_KEY = "erp:parametros_nomina";

// Tarifas mínimas de ARL por clase de riesgo (Decreto 1607 de 2002), % sobre el IBC.
export const DEFAULT_TABLA_ARL = { I: 0.522, II: 1.044, III: 2.436, IV: 4.350, V: 6.960 };

// Tramos del Fondo de Solidaridad Pensional (Ley 100/1993 Art. 27-28): aplica solo
// sobre el exceso de salario por encima de 4 SMLMV, tomando el % del tramo en el que
// cae el total del IBC expresado en SMLMV.
export const DEFAULT_FONDO_SOLIDARIDAD_TRAMOS = [
  { desde_smlmv: 4,  hasta_smlmv: 16,   pct: 1.0 },
  { desde_smlmv: 16, hasta_smlmv: 17,   pct: 1.2 },
  { desde_smlmv: 17, hasta_smlmv: 18,   pct: 1.4 },
  { desde_smlmv: 18, hasta_smlmv: 19,   pct: 1.6 },
  { desde_smlmv: 19, hasta_smlmv: 20,   pct: 1.8 },
  { desde_smlmv: 20, hasta_smlmv: null, pct: 2.0 }
];

export const DEFAULT_PARAMETROS_NOMINA = {
  anio_vigencia: new Date().getFullYear(),
  smlmv: 1423500,                      // Salario Mínimo Legal Mensual Vigente (decreto de cada diciembre)
  auxilio_transporte: 200000,          // Auxilio de transporte vigente
  auxilio_transporte_tope_smlmv: 2,    // Aplica a quien devengue hasta 2 SMLMV
  salud_empleado_pct: 4,
  pension_empleado_pct: 4,
  fondo_solidaridad_tramos: DEFAULT_FONDO_SOLIDARIDAD_TRAMOS,
  salud_patronal_pct: 8.5,
  pension_patronal_pct: 12,
  tabla_arl: DEFAULT_TABLA_ARL,
  sena_pct: 2,
  icbf_pct: 3,
  caja_compensacion_pct: 4,
  exonera_sena_icbf: false,            // Ley 1607 de 2012 — marcar si la empresa aplica exoneración
  exonera_salud_patronal: false,       // Ley 1607 de 2012 — marcar si la empresa aplica exoneración
  cesantias_pct: 8.33,
  intereses_cesantias_pct: 1,          // Mensual (equivale a 12% anual sobre cesantías)
  prima_pct: 8.33,
  vacaciones_pct: 4.17,
  periodicidad_default: "mensual",     // mensual | quincenal
  notas: "Actualizar cada diciembre/enero según decreto de SMLMV y auxilio de transporte vigentes."
};

export const PUC_DEFAULT = [
  // ═══════════════════════════════════════════════════════
  // CLASE 1 — ACTIVO (Decreto 2650 / NIIF Grupo 2)
  // ═══════════════════════════════════════════════════════
  // Grupo 11 — Disponible
  { codigo:"1105", nombre:"Caja general", naturaleza:"D" },
  { codigo:"1110", nombre:"Bancos — cuentas corrientes y de ahorro", naturaleza:"D" },
  // Grupo 12 — Inversiones (corto plazo)
  { codigo:"1205", nombre:"Inversiones en acciones — corto plazo", naturaleza:"D" },
  // Grupo 13 — Deudores (CxC)
  { codigo:"1305", nombre:"Clientes nacionales — cuentas por cobrar comerciales", naturaleza:"D" },
  { codigo:"1310", nombre:"Clientes del exterior", naturaleza:"D" },
  { codigo:"1330", nombre:"Anticipos y avances entregados a terceros", naturaleza:"D" },
  { codigo:"1355", nombre:"IVA descontable en compras (impuesto descontable)", naturaleza:"D" },
  { codigo:"1360", nombre:"Reclamaciones a compañías aseguradoras", naturaleza:"D" },
  { codigo:"1365", nombre:"Retención en la fuente a favor — practicada por clientes", naturaleza:"D" },
  { codigo:"1366", nombre:"ReteIVA a favor — practicada por clientes", naturaleza:"D" },
  { codigo:"1368", nombre:"ReteICA a favor — practicada por clientes", naturaleza:"D" },
  { codigo:"1370", nombre:"Cuentas por cobrar a trabajadores", naturaleza:"D" },
  { codigo:"1380", nombre:"Deudores varios", naturaleza:"D" },
  // Grupo 14 — Inventarios
  { codigo:"1405", nombre:"Materias primas e insumos", naturaleza:"D" },
  { codigo:"1430", nombre:"Productos terminados", naturaleza:"D" },
  { codigo:"1435", nombre:"Inventario de mercancías no fabricadas por la empresa", naturaleza:"D" },
  { codigo:"1455", nombre:"Materiales, repuestos y accesorios", naturaleza:"D" },
  // Grupo 15 — Propiedad, planta y equipo
  { codigo:"1504", nombre:"Terrenos", naturaleza:"D" },
  { codigo:"1516", nombre:"Construcciones y edificaciones", naturaleza:"D" },
  { codigo:"1520", nombre:"Maquinaria y equipo", naturaleza:"D" },
  { codigo:"1524", nombre:"Equipo de oficina", naturaleza:"D" },
  { codigo:"1528", nombre:"Equipo de cómputo y comunicaciones", naturaleza:"D" },
  { codigo:"1540", nombre:"Flota y equipo de transporte", naturaleza:"D" },
  { codigo:"1592", nombre:"Depreciación acumulada (CR)", naturaleza:"C" },
  // Grupo 16 — Intangibles
  { codigo:"1605", nombre:"Crédito mercantil (Goodwill)", naturaleza:"D" },
  { codigo:"1610", nombre:"Marcas, patentes y concesiones", naturaleza:"D" },
  // Grupo 17 — Diferidos
  { codigo:"1705", nombre:"Gastos pagados por anticipado", naturaleza:"D" },
  { codigo:"1710", nombre:"Seguros pagados por anticipado", naturaleza:"D" },
  // ═══════════════════════════════════════════════════════
  // CLASE 2 — PASIVO
  // ═══════════════════════════════════════════════════════
  // Grupo 21 — Obligaciones financieras
  { codigo:"2105", nombre:"Bancos nacionales — préstamos y créditos", naturaleza:"C" },
  { codigo:"2110", nombre:"Corporaciones financieras", naturaleza:"C" },
  // Grupo 22 — Proveedores
  { codigo:"2205", nombre:"Proveedores nacionales — cuentas por pagar comerciales (CxP)", naturaleza:"C" },
  { codigo:"2210", nombre:"Proveedores del exterior", naturaleza:"C" },
  // Grupo 23 — Cuentas por pagar
  { codigo:"2305", nombre:"Gastos acumulados por pagar — nómina y prestaciones", naturaleza:"C" },
  { codigo:"2315", nombre:"Costos y gastos por pagar — honorarios y servicios", naturaleza:"C" },
  { codigo:"2330", nombre:"Retención en la fuente — empleados (renta)", naturaleza:"C" },
  { codigo:"2335", nombre:"Retención en la fuente — ICA empleados", naturaleza:"C" },
  // Grupo 23 — Retenciones que la empresa PRACTICA a proveedores (pasivo DIAN)
  { codigo:"2365", nombre:"Retención en la fuente por pagar — practicada a proveedores", naturaleza:"C" },
  { codigo:"2367", nombre:"ReteIVA por pagar — practicada a proveedores", naturaleza:"C" },
  { codigo:"2368", nombre:"ReteICA por pagar — practicada a proveedores", naturaleza:"C" },
  // Grupo 23 — Aportes de nómina por pagar (seguridad social y parafiscales)
  { codigo:"2370", nombre:"Aportes a EPS por pagar", naturaleza:"C" },
  { codigo:"2371", nombre:"Aportes a fondos de pensión por pagar", naturaleza:"C" },
  { codigo:"2372", nombre:"Aportes ARL por pagar", naturaleza:"C" },
  { codigo:"2373", nombre:"Aportes parafiscales por pagar (SENA, ICBF, Caja de Compensación)", naturaleza:"C" },
  // Grupo 24 — Impuestos, gravámenes y tasas
  { codigo:"2404", nombre:"Impuesto de renta y complementarios por pagar", naturaleza:"C" },
  { codigo:"2408", nombre:"IVA generado por pagar (IVA en ventas)", naturaleza:"C" },
  { codigo:"2412", nombre:"Impuesto de industria y comercio por pagar", naturaleza:"C" },
  { codigo:"2416", nombre:"Impuesto predial y complementarios", naturaleza:"C" },
  // Grupo 25 — Obligaciones laborales
  { codigo:"2505", nombre:"Salarios por pagar", naturaleza:"C" },
  { codigo:"2510", nombre:"Cesantías consolidadas", naturaleza:"C" },
  { codigo:"2515", nombre:"Intereses sobre cesantías", naturaleza:"C" },
  { codigo:"2520", nombre:"Prima de servicios", naturaleza:"C" },
  { codigo:"2525", nombre:"Vacaciones consolidadas", naturaleza:"C" },
  // Grupo 26 — Pasivos estimados y provisiones
  { codigo:"2605", nombre:"Provisión para obligaciones fiscales", naturaleza:"C" },
  { codigo:"2610", nombre:"Provisión para prestaciones sociales", naturaleza:"C" },
  { codigo:"2615", nombre:"Provisión impuesto a las ganancias por pagar", naturaleza:"C" },
  // Grupo 27 — Diferidos (pasivo)
  { codigo:"2705", nombre:"Ingresos recibidos por anticipado", naturaleza:"C" },
  { codigo:"2710", nombre:"Anticipos de clientes", naturaleza:"C" },
  // ═══════════════════════════════════════════════════════
  // CLASE 3 — PATRIMONIO
  // ═══════════════════════════════════════════════════════
  { codigo:"3105", nombre:"Capital suscrito y pagado", naturaleza:"C" },
  { codigo:"3115", nombre:"Capital social / aportes socios", naturaleza:"C" },
  { codigo:"3205", nombre:"Prima en colocación de acciones o cuotas", naturaleza:"C" },
  { codigo:"3305", nombre:"Reserva legal", naturaleza:"C" },
  { codigo:"3310", nombre:"Reservas estatutarias", naturaleza:"C" },
  { codigo:"3315", nombre:"Reservas ocasionales", naturaleza:"C" },
  { codigo:"3405", nombre:"Utilidades de ejercicios anteriores (superávit acumulado)", naturaleza:"C" },
  { codigo:"3505", nombre:"Pérdidas de ejercicios anteriores (déficit acumulado)", naturaleza:"D" },
  { codigo:"3605", nombre:"Utilidad del ejercicio corriente", naturaleza:"C" },
  { codigo:"3610", nombre:"Pérdida del ejercicio corriente", naturaleza:"D" },
  // ═══════════════════════════════════════════════════════
  // CLASE 4 — INGRESOS
  // ═══════════════════════════════════════════════════════
  // Grupo 41 — Ingresos operacionales
  { codigo:"4135", nombre:"Ingresos operacionales — comercio al por mayor y al por menor", naturaleza:"C" },
  { codigo:"4155", nombre:"Ingresos operacionales — servicios prestados", naturaleza:"C" },
  // Grupo 41 — Descuentos y devoluciones (contra-ingresos)
  { codigo:"4175", nombre:"Devoluciones en ventas — notas crédito (contra-ingreso)", naturaleza:"D" },
  { codigo:"4180", nombre:"Descuentos comerciales en ventas (contra-ingreso)", naturaleza:"D" },
  // Grupo 42 — Ingresos no operacionales
  { codigo:"4205", nombre:"Ingresos financieros — rendimientos y corrección monetaria", naturaleza:"C" },
  { codigo:"4210", nombre:"Ingresos financieros — descuentos por pronto pago obtenidos", naturaleza:"C" },
  { codigo:"4215", nombre:"Dividendos e ingresos por participaciones", naturaleza:"C" },
  { codigo:"4245", nombre:"Ingresos por arrendamientos", naturaleza:"C" },
  { codigo:"4250", nombre:"Utilidad en venta de activos fijos", naturaleza:"C" },
  { codigo:"4295", nombre:"Otros ingresos no operacionales", naturaleza:"C" },
  // ═══════════════════════════════════════════════════════
  // CLASE 5 — GASTOS
  // ═══════════════════════════════════════════════════════
  // Grupo 51 — Gastos operacionales de administración
  { codigo:"5105", nombre:"Gastos de personal — sueldos y salarios (admón.)", naturaleza:"D" },
  { codigo:"5110", nombre:"Gastos de personal — prestaciones sociales (admón.)", naturaleza:"D" },
  { codigo:"5115", nombre:"Gastos de personal — aportes parafiscales (admón.)", naturaleza:"D" },
  { codigo:"5120", nombre:"Honorarios profesionales (admón.)", naturaleza:"D" },
  { codigo:"5125", nombre:"Impuestos y gravámenes (ICA, predial — admón.)", naturaleza:"D" },
  { codigo:"5130", nombre:"Arrendamientos de inmuebles (admón.)", naturaleza:"D" },
  { codigo:"5135", nombre:"Servicios públicos, telecomunicaciones y mantenimiento (admón.)", naturaleza:"D" },
  { codigo:"5140", nombre:"Gastos de viaje y representación (admón.)", naturaleza:"D" },
  { codigo:"5145", nombre:"Depreciaciones — equipo de oficina y cómputo (admón.)", naturaleza:"D" },
  { codigo:"5150", nombre:"Amortizaciones — intangibles (admón.)", naturaleza:"D" },
  { codigo:"5160", nombre:"Seguros (admón.)", naturaleza:"D" },
  { codigo:"5195", nombre:"Gastos diversos de administración", naturaleza:"D" },
  // Grupo 52 — Gastos operacionales de ventas
  { codigo:"5205", nombre:"Gastos de personal — sueldos fuerza de ventas", naturaleza:"D" },
  { codigo:"5210", nombre:"Gastos de personal — comisiones de vendedores", naturaleza:"D" },
  { codigo:"5215", nombre:"Publicidad y propaganda", naturaleza:"D" },
  { codigo:"5220", nombre:"Gastos de viaje — fuerza de ventas", naturaleza:"D" },
  { codigo:"5225", nombre:"Fletes y acarreos en ventas", naturaleza:"D" },
  { codigo:"5295", nombre:"Otros gastos de ventas", naturaleza:"D" },
  // Grupo 53 — Gastos no operacionales / financieros
  { codigo:"5305", nombre:"Gastos financieros — descuentos por pronto pago concedidos", naturaleza:"D" },
  { codigo:"5310", nombre:"Gastos financieros — intereses bancarios y de crédito", naturaleza:"D" },
  { codigo:"5315", nombre:"Diferencia en cambio (pérdida)", naturaleza:"D" },
  { codigo:"5320", nombre:"Pérdida en venta y retiro de activos fijos", naturaleza:"D" },
  { codigo:"5395", nombre:"Gastos no operacionales diversos", naturaleza:"D" },
  // Grupo 54 — Impuesto de renta
  { codigo:"5405", nombre:"Gasto impuesto de renta y complementarios", naturaleza:"D" },
  // ═══════════════════════════════════════════════════════
  // CLASE 6 — COSTOS DE VENTAS
  // ═══════════════════════════════════════════════════════
  { codigo:"6135", nombre:"Costo de ventas — mercancías no fabricadas (costo de lo vendido)", naturaleza:"D" },
  { codigo:"6140", nombre:"Costo de ventas — costos asociados a órdenes (transporte, fletes, servicios de terceros)", naturaleza:"D" },
  { codigo:"6145", nombre:"Costo de ventas — servicios prestados", naturaleza:"D" },
  // ═══════════════════════════════════════════════════════
  // CLASE 7 — COSTOS DE PRODUCCIÓN (para empresas industriales)
  // ═══════════════════════════════════════════════════════
  { codigo:"7105", nombre:"Materia prima e insumos utilizados en producción", naturaleza:"D" },
  { codigo:"7205", nombre:"Mano de obra directa en producción", naturaleza:"D" },
  { codigo:"7305", nombre:"Costos indirectos de fabricación", naturaleza:"D" }
];


export const DOC_INFO = {
  CT:   { label:"Cotización",          plural:"Cotizaciones",          code:"MTHC-010", color:DOC_COLORS.CT,   key:"cotizaciones", prefix:"CT",  icon:"📋", convertsTo:["OP"] },
  OP:   { label:"Orden de Producción", plural:"Órdenes de Producción", code:"MTHC-009", color:DOC_COLORS.OP,   key:"pedidos",      prefix:"OP",  icon:"🔧", convertsTo:["OCP","RC"] },
  OCP:  { label:"Orden de Compra",     plural:"Órdenes de Compra",     code:"MTHC-008", color:DOC_COLORS.OCP,  key:"ocp",          prefix:"OCP", icon:"🛒", convertsTo:[] },
  RC:   { label:"Remisión",            plural:"Remisiones",            code:"MTHC-011", color:DOC_COLORS.RC,   key:"remisiones",   prefix:"RS",  icon:"📮", convertsTo:["FACT"] },
  FACT: { label:"Factura Electrónica", plural:"Facturas",              code:"MTHC-012", color:DOC_COLORS.FACT, key:"facturas",     prefix:"FE",  icon:"💰", convertsTo:[] },
  NC:   { label:"Nota Crédito",        plural:"Notas Crédito",         code:"MTHC-013", color:DOC_COLORS.NC,   key:"notas_credito",prefix:"NC",  icon:"🧾", convertsTo:[] }
};

export const ESTADOS = {
  CT:   { borrador:"📝 Borrador", enviada:"📤 Enviada", aprobada:"✅ Aprobada", completada:"✅ Completada", convertida:"➡️ Convertida a OP", rechazada:"❌ Rechazada" },
  OP:   { borrador:"📝 Borrador", confirmada:"✅ Confirmada", en_proceso:"⚙️ En proceso", completada:"✅ Completada", convertida:"➡️ Convertida", cancelada:"❌ Cancelada" },
  OCP:  { borrador:"📝 Borrador", enviada:"📤 Enviada", recibida:"✅ Recibida", completada:"✅ Completada", cancelada:"❌ Cancelada" },
  RC:   { borrador:"📝 Borrador", entregada:"📦 Entregada", completada:"✅ Completada", facturada:"💰 Facturada", cancelada:"❌ Cancelada" },
  FACT: { borrador:"📝 Borrador", emitida:"✅ Emitida", pagada:"💵 Pagada", vencida:"⚠️ Vencida", anulada:"❌ Anulada" },
  NC:   { emitida:"✅ Emitida", anulada:"❌ Anulada" }
};

export const ESTADO_COLOR = {
  borrador:"#6b7280", enviada:"#2B4A82", aprobada:"#10b981", confirmada:"#10b981",
  en_proceso:"#f59e0b", convertida:"#4C5FA8", completada:"#10b981", rechazada:"#ef4444", cancelada:"#ef4444",
  recibida:"#10b981", entregada:"#10b981", facturada:"#10b981",
  emitida:"#059669", pagada:"#047857", vencida:"#d97706", anulada:"#ef4444"
};

export const ESTADO_PAGO = {
  por_pagar: { label:"💸 Por pagar",  color:"#f59e0b", bg:"#fef3c7" },
  pagada:    { label:"✅ Pagada",     color:"#10b981", bg:"#d1fae5" },
  parcial:   { label:"⏳ Pago parcial", color:"#2B4A82", bg:"#e4eaf5" }
};

export const MEDIOS_PAGO = [
  { value:"transferencia", label:"🏦 Transferencia bancaria" },
  { value:"efectivo",      label:"💵 Efectivo" },
  { value:"cheque",        label:"📝 Cheque" },
  { value:"tarjeta",       label:"💳 Tarjeta de crédito/débito" },
  { value:"pse",           label:"🌐 PSE / Pago electrónico" },
  { value:"otro",          label:"📄 Otro" }
];

export const MEDIOS_DESC_FIN = [
  { value:"transferencia", label:"🏦 Transferencia bancaria" },
  { value:"tarjeta",       label:"💳 Tarjeta de crédito/débito" },
  { value:"cheque",        label:"📝 Cheque" },
  { value:"pse",           label:"🌐 PSE / Pago electrónico" },
  { value:"otro",          label:"📄 Otro" }
];

export const ALERTA_GRUPO_LABELS = {
  OP:      { titulo: "🔧 Órdenes de Producción — Entregas",                color: DOC_INFO.OP.color },
  CTVIG:   { titulo: "📋 Cotizaciones — Vigencia",                         color: DOC_INFO.CT.color },
  CTSEG:   { titulo: "📤 Cotizaciones — Seguimiento",                      color: DOC_INFO.CT.color },
  OCP:     { titulo: "💸 Cuentas por Pagar — Descuento financiero",        color: DOC_INFO.OCP.color },
  RESDIAN: { titulo: "🧾 Resolución DIAN — Vigencia de facturación",       color: "#dc2626" },
  MATRICULA: { titulo: "🏛️ Cámara de Comercio — Renovación de matrícula",  color: "#b45309" }
};

export const PREFIJOS_CONSECUTIVOS = [
  { prefijo: "CT",  label: "Cotizaciones" },
  { prefijo: "OP",  label: "Órdenes de Producción" },
  { prefijo: "OCP", label: "Órdenes de Compra" },
  { prefijo: "RS",  label: "Remisiones" },
  { prefijo: "FE",  label: "Facturas electrónicas (Dataico)" },
  { prefijo: "FL",  label: "Facturas locales (sin Dataico)" },
  { prefijo: "CXP", label: "Cuentas por pagar directas" },
  { prefijo: "CC",  label: "Cuentas de cobro" },
  { prefijo: "NC",  label: "Notas Crédito" },
  { prefijo: "AST", label: "Asientos contables (automáticos)" },
  { prefijo: "MAN", label: "Asientos manuales" },
  { prefijo: "NOM", label: "Liquidaciones de nómina" },
  { prefijo: "DEP", label: "Liquidaciones de depreciación de activos fijos" }
];

export const CUENTAS_RETENCION = {
  "2365": { label:"ReteFuente practicada a proveedores",  corto:"ReteFuente", base:"subtotal" },
  "2367": { label:"ReteIVA practicada a proveedores",     corto:"ReteIVA",    base:"iva"      },
  "2368": { label:"ReteICA practicada a proveedores",     corto:"ReteICA",    base:"subtotal" }
};

export const CUENTAS_RETENCION_CLIENTE = {
  "1365": { label:"ReteFuente practicada por clientes",   corto:"ReteFuente", base:"subtotal" },
  "1366": { label:"ReteIVA practicada por clientes",      corto:"ReteIVA",    base:"iva"      },
  "1368": { label:"ReteICA practicada por clientes",      corto:"ReteICA",    base:"subtotal" }
};

export const CONCEPTOS_1001 = {
  "5001": "Salarios y demás pagos laborales",
  "5002": "Honorarios",
  "5003": "Comisiones",
  "5004": "Servicios",
  "5005": "Arrendamientos",
  "5006": "Intereses y rendimientos financieros",
  "5007": "Pagos a establecimientos de crédito",
  "5010": "Compras con retención",
  "5011": "Compras sin retención (informativas)",
  "5012": "Dividendos y participaciones pagados en acciones",
  "5013": "Dividendos y participaciones pagados en efectivo",
  "5014": "Pagos al exterior",
  "5020": "Ingresos propios (ESAL)",
  "5025": "Compra de activos fijos",
  "5041": "Pagos a personas naturales del régimen simple",
  "5047": "Contratos de construcción y urbanización",
  "5051": "Pagos por exportaciones",
  "5071": "Otras retenciones",
};

export const EXOGENA_FORMATOS = {
  "1001": { label:"Formato 1001 - Pagos/compras y retenciones practicadas" },
  "1007": { label:"Formato 1007 - Ingresos recibidos" },
  "1008": { label:"Formato 1008 - Saldos cuentas por pagar (31 dic)" },
  "1009": { label:"Formato 1009 - Saldos cuentas por cobrar (31 dic)" }
};

export const IA_MODELOS = [
  { value:"claude-sonnet-4-6", label:"Claude Sonnet 4.6 (recomendado)" },
  { value:"claude-opus-4-6",   label:"Claude Opus 4.6 (más potente, más costoso)" },
  { value:"claude-haiku-4-5-20251001", label:"Claude Haiku 4.5 (más rápido y económico)" }
];

export const COMISIONES_KEY = "erp:tabla_comisiones";

export const DEFAULT_TABLA_COMISIONES = {
  rangosVendedor: [
    { id: "junior",  label: "Vendedor Junior",  descripcion: "Hasta 12 meses de experiencia" },
    { id: "senior",  label: "Vendedor Senior",   descripcion: "Más de 12 meses de experiencia" },
    { id: "general", label: "General",           descripcion: "Aplica si el vendedor no tiene rango asignado" }
  ],
  reglas: [
    // --- GENERAL ---
    { id: "g1", rangoVendedor: "general", margenMin: 24.5,  margenMax: null,  comisionPct: 5,   label: "Margen > 24.5%" },
    { id: "g2", rangoVendedor: "general", margenMin: 19.59, margenMax: 24.49, comisionPct: 3,   label: "Margen 19.59% – 24.49%" },
    { id: "g3", rangoVendedor: "general", margenMin: 15,    margenMax: 19.49, comisionPct: 2,   label: "Margen 15% – 19.49%" },
    // --- JUNIOR ---
    { id: "j1", rangoVendedor: "junior",  margenMin: 24.5,  margenMax: null,  comisionPct: 4,   label: "Margen > 24.5%" },
    { id: "j2", rangoVendedor: "junior",  margenMin: 19.59, margenMax: 24.49, comisionPct: 2.5, label: "Margen 19.59% – 24.49%" },
    { id: "j3", rangoVendedor: "junior",  margenMin: 15,    margenMax: 19.49, comisionPct: 1.5, label: "Margen 15% – 19.49%" },
    // --- SENIOR ---
    { id: "s1", rangoVendedor: "senior",  margenMin: 24.5,  margenMax: null,  comisionPct: 6,   label: "Margen > 24.5%" },
    { id: "s2", rangoVendedor: "senior",  margenMin: 19.59, margenMax: 24.49, comisionPct: 4,   label: "Margen 19.59% – 24.49%" },
    { id: "s3", rangoVendedor: "senior",  margenMin: 15,    margenMax: 19.49, comisionPct: 3,   label: "Margen 15% – 19.49%" }
  ]
};
